import React from "react";
import { formatINR, transModeLabel, groupEwbNo, docTypeLabel } from "./ewayBillUtils";
import { Barcode128 } from "../../utils/barcode128";
import { QrCodeSvg } from "../../utils/QrCodeSvg";
import { es, SectionTitle, Field } from "./EwayBillPrintStyles";

const money = (v) => formatINR(v ?? 0);
const fmt3 = (v) => (v === null || v === undefined || isNaN(Number(v)) ? "0.000" : Number(v).toFixed(3));

// NIC QR date: "04/07/2025 09:39:00 AM" (dd/MM/yyyy) -> "7/4/2025 9:39:00 AM" (M/d/yyyy, no leading zeros).
function qrDate(ewayBillDate) {
    const m = String(ewayBillDate || "").trim()
        .match(/^(\d{2})\/(\d{2})\/(\d{4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?$/i);
    if (!m) return ewayBillDate || "";
    const [, dd, MM, yyyy, hh, min, ss, ap] = m;
    return `${parseInt(MM, 10)}/${parseInt(dd, 10)}/${yyyy} ${parseInt(hh, 10)}:${min}:${ss || "00"}${ap ? " " + ap.toUpperCase() : ""}`;
}

// Tax-rate cell: "C+S+I+Cess+Cess Non.Advol", NE when not applicable.
function rateStr(it) {
    const cess = it.cessRate === null || it.cessRate === undefined ? "NE" : fmt3(it.cessRate);
    const cessNa = it.cessNonAdvolRate === null || it.cessNonAdvolRate === undefined ? "0.00" : Number(it.cessNonAdvolRate).toFixed(2);
    return `${fmt3(it.cgstRate)}+${fmt3(it.sgstRate)}+${fmt3(it.igstRate)}+${cess}+${cessNa}`;
}

export function EwayBillPrintSheet({ ewayBill }) {
    const p = ewayBill.requestPayload || {};
    const cancelled = String(ewayBill.status || "").toUpperCase() === "CANCELLED";
    const items = p.itemList || [];

    const totals = [
        ["Tot. Tax'ble Amt", p.totalValue],
        ["CGST Amt", p.cgstValue],
        ["SGST Amt", p.sgstValue],
        ["IGST Amt", p.igstValue],
        ["CESS Amt", p.cessValue],
        ["CESS Non.Advol Amt", p.cessNonAdvolValue],
        ["Other Amt", p.otherValue],
        ["Total Inv.Amt", p.totInvValue],
    ];

    return (
        <div className="print-receipt bg-white" style={es.container}>
            {cancelled && <div style={es.watermark}>CANCELLED</div>}

            {/* Header */}
            <div style={es.headerRow}>
                <div>
                    <div style={es.title}>e-Way Bill</div>
                    {cancelled && <span style={es.cancelBadge}>CANCELLED</span>}
                </div>
                <div style={es.qrBox}>
                    {/* NIC QR content: ewbNo/generatorGstin/M-d-yyyy h:mm:ss AM/PM */}
                    <QrCodeSvg
                        value={`${ewayBill.ewayBillNo || ""}/${p.fromGstin || ""}/${qrDate(ewayBill.ewayBillDate)}`}
                        size={96}
                    />
                </div>
            </div>

            {/* 1. E-WAY BILL Details */}
            <SectionTitle>1. E-WAY BILL Details</SectionTitle>
            <div style={es.grid4}>
                <Field label="eWay Bill No" value={groupEwbNo(ewayBill.ewayBillNo)} strong />
                <Field label="Generated Date" value={ewayBill.ewayBillDate} />
                <Field label="Generated By" value={p.fromGstin} />
                <Field label="Valid Upto" value={ewayBill.validUpto} />
            </div>
            <div style={es.grid4}>
                <Field label="Mode" value={transModeLabel(p.transMode || ewayBill.transMode)} />
                <Field label="Approx Distance" value={`${p.transDistance ?? ewayBill.transDistance ?? "-"}km`} />
                <Field label="Type" value="Outward - Supply" />
                <Field label="Transaction Type" value={p.transactionType || "Regular"} />
            </div>
            <div style={es.grid4}>
                <Field
                    label="Document Details"
                    value={`${docTypeLabel(p.docType)} - ${p.docNo || "-"} - ${p.docDate || "-"}`}
                />
            </div>

            {/* 2. Address Details */}
            <SectionTitle>2. Address Details</SectionTitle>
            <div style={es.grid2}>
                <div style={es.addrCol}>
                    <div style={es.addrHead}>From</div>
                    <div style={es.addrLine}><strong>GSTIN :</strong> {p.fromGstin || "URP"}</div>
                    <div style={es.addrLine}>{p.fromTrdName || "-"}</div>
                    <div style={es.addrLine}>State Code: {p.fromStateCode || "-"}</div>
                    <div style={es.addrDivider}>:: Dispatch From ::</div>
                    <div style={es.addrLine}>{[p.fromAddr1, p.fromAddr2].filter(Boolean).join(", ") || "-"}</div>
                    <div style={es.addrLine}>{p.fromPlace || "-"}{p.fromPincode ? `, ${p.fromPincode}` : ""}</div>
                </div>
                <div style={es.addrCol}>
                    <div style={es.addrHead}>To</div>
                    <div style={es.addrLine}><strong>GSTIN :</strong> {p.toGstin || "URP"}</div>
                    <div style={es.addrLine}>{p.toTrdName || "-"}</div>
                    <div style={es.addrLine}>State Code: {p.toStateCode || "-"}</div>
                    <div style={es.addrDivider}>:: Ship To ::</div>
                    <div style={es.addrLine}>{[p.toAddr1, p.toAddr2].filter(Boolean).join(", ") || "-"}</div>
                    <div style={es.addrLine}>{p.toPlace || "-"}{p.toPincode ? `, ${p.toPincode}` : ""}</div>
                </div>
            </div>

            {/* 3. Goods Details */}
            <SectionTitle>3. Goods Details</SectionTitle>
            <table style={es.table}>
                <thead>
                    <tr style={es.theadRow}>
                        <th style={es.th}>HSN Code</th>
                        <th style={{ ...es.th, textAlign: "left" }}>Product Name &amp; Desc.</th>
                        <th style={es.th}>Quantity</th>
                        <th style={es.thNum}>Taxable Amount Rs.</th>
                        <th style={es.th}>Tax Rate (C+S+I+Cess+Cess Non.Advol)</th>
                    </tr>
                </thead>
                <tbody>
                    {items.length === 0 ? (
                        <tr><td style={es.td} colSpan={5}>No item details available</td></tr>
                    ) : items.map((it, i) => (
                        <tr key={i}>
                            <td style={es.td}>{it.hsnCode || "-"}</td>
                            <td style={{ ...es.td, textAlign: "left" }}>{it.productName || "-"}</td>
                            <td style={es.td}>{it.quantity ?? "-"} {it.qtyUnit || ""}</td>
                            <td style={es.tdNum}>{money(it.taxableAmount)}</td>
                            <td style={es.td}>{rateStr(it)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <div style={es.totalsStrip}>
                {totals.map(([label, val]) => (
                    <div key={label} style={es.totalBox}>
                        <div style={es.totalLabel}>{label}</div>
                        <div style={es.totalVal}>{money(val)}</div>
                    </div>
                ))}
            </div>

            {/* 4. Transportation Details */}
            <SectionTitle>4. Transportation Details</SectionTitle>
            <div style={es.grid2}>
                <Field label="Transporter ID & Name" value={`${p.transporterId || "-"} ${p.transporterName ? "- " + p.transporterName : ""}`} />
                <Field label="Transporter Doc. No & Date" value={`${p.transDocNo || "-"}${p.transDocDate ? " - " + p.transDocDate : ""}`} />
            </div>

            {/* 5. Vehicle Details */}
            <SectionTitle>5. Vehicle Details</SectionTitle>
            <table style={es.table}>
                <thead>
                    <tr style={es.theadRow}>
                        <th style={es.th}>Mode</th>
                        <th style={es.th}>Vehicle / Trans Doc No &amp; Dt.</th>
                        <th style={es.th}>From</th>
                        <th style={es.th}>Entered Date</th>
                        <th style={es.th}>Entered By</th>
                        <th style={es.th}>CEWB No. (If any)</th>
                        <th style={es.th}>Multi Veh.Info (If any)</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td style={es.td}>{transModeLabel(p.transMode || ewayBill.transMode)}</td>
                        <td style={es.td}>{p.vehicleNo || ewayBill.vehicleNo || "-"}</td>
                        <td style={es.td}>{p.fromPlace || "-"}</td>
                        <td style={es.td}>{ewayBill.ewayBillDate || "-"}</td>
                        <td style={es.td}>{p.fromGstin || "-"}</td>
                        <td style={es.td}>-</td>
                        <td style={es.td}>-</td>
                    </tr>
                </tbody>
            </table>

            {/* Barcode */}
            <div style={es.barcodeWrap}>
                <Barcode128 value={String(ewayBill.ewayBillNo || "")} height={48} moduleWidth={1.4} />
                <div style={es.barcodeText}>{groupEwbNo(ewayBill.ewayBillNo)}</div>
            </div>

            {/* Branding footer */}
            <div style={es.brandFooter}>
                Powered by Revcon Technologies &bull; revcontech.com
            </div>

            <style>{`
                @media print {
                    @page { size: A4; margin: 8mm; }
                    body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
                    .d-print-none { display: none !important; }
                    .print-receipt { width: 100% !important; max-width: none !important; margin: 0 !important; box-shadow: none !important; }
                    .app-aside, .app-sidebar, .app-header, .app-footer, .app-toolbar { display: none !important; }
                    .app-wrapper, .app-content { padding: 0 !important; margin: 0 !important; }
                }
            `}</style>
        </div>
    );
}
